import Vue from "vue";

import { Component, Prop, Watch } from "@/decorators";
import ConfiguracaoRotuloCamposModel from "@/models/configuracaoRotuloCamposModel";
import ConfiguracaoRotuloModel from "@/models/configuracaoRotuloModel";
import Alinhamento from "@/models/enum/alinhamento";
import { Fonte } from "@/models/enum/fonte";
import ConteudoQrCode from "@/models/enum/rotulo/conteudoQrCode";
import FormatoImpressaoRotulo from "@/models/enum/rotulo/formatoImpressaoRotulo";
import PosicaoDadosFranquia from "@/models/enum/rotulo/posicaoDadosFranquia";
import PosicaoRotulo from "@/models/enum/rotulo/posicaoRotulo";
import CodigosIdentificacaoService from "@/services/codigosIdentificacaoService";

// #################################################################################################################
// IMPORTANTE: O estilo do rótulo deve ser inline usando o atributo style. Caso contrário, o rótulo será renderizado
//             sem estilo na janela de impressão ou no QZ Tray.
// #################################################################################################################

/** Usado quando a configuração não tem tamanho de fonte válido — fonte zerada deixa o texto invisível. */
const FONTE_MINIMA = 7;

/** Usado quando a configuração não tem quantidade de colunas válida — zero anula o cálculo das linhas. */
const QUANTIDADE_MINIMA_COLUNAS = 1;

@Component
export default class RotuloComponent extends Vue {
    @Prop(ConfiguracaoRotuloModel) model: ConfiguracaoRotuloModel;
    @Prop(String) dadosFranquia: string;
    @Prop(String) manipulacaoKey: string;
    @Prop(String) dadosResponsavelTecnicoFranquia: string;
    @Prop(String) codigoQrValue: string;
    @Prop(Boolean) CalledByShortCut: boolean;
    @Prop({ type: Boolean, default: false }) selected: boolean;
    @Prop({ type: Number, default: 1 }) scaleFactor: number;

    private codigosIdentificacaoService = new CodigosIdentificacaoService();

    private qrCodeBase64 = "";
    private barCodeBase64 = "";

    private getStringExibicao(model: ConfiguracaoRotuloCamposModel) {
        const label = model.label ?? "";

        if (this.CalledByShortCut) {
            if (model.value && label) {
                return `${label}: ${model.value ?? ""}`;
            }
            if (model.value) {
                return model.value;
            }
        } else if (label == "") {
            return model.campo;
        }
        return label;
    }

    // Para uso no template
    private PosicaoRotulo = PosicaoRotulo;

    private getValorPositivo(valor: number, padrao: number) {
        return Number(valor) > 0 ? Number(valor) : padrao;
    }

    private get linhas() {
        const array = this.model.campos;
        // Sem a guarda, quantidadeColunas zerada/nula gera divisão por zero: o índice da linha vira NaN, nenhuma
        // linha entra no array e o rótulo é renderizado completamente vazio.
        const tamanhoLinha = this.getValorPositivo(this.model.quantidadeColunas, QUANTIDADE_MINIMA_COLUNAS);
        const linhas: Array<Array<ConfiguracaoRotuloCamposModel>> = [];
        let totalColunas = 0;

        for (const item of array) {
            const colunasCampo = this.getValorPositivo(item.quantidadeColunas, QUANTIDADE_MINIMA_COLUNAS);
            item.quantidadeColunas = Math.min(colunasCampo, tamanhoLinha);
            const linhaAtual = Math.floor(totalColunas / tamanhoLinha);
            linhas[linhaAtual] = [...(linhas[linhaAtual] || []), item];
            totalColunas += item.quantidadeColunas;
        }

        return linhas;
    }

    // Style getters usados nos atributos style do template
    // ==============================================================================================

    private adjustFontSize(size: number) {
        // Fonte zerada/nula renderiza o texto com font-size 0, ou seja, invisível dentro do rótulo.
        return this.getValorPositivo(size, FONTE_MINIMA) / 4;
    }

    private adjustLineHeight(lineHeight: number) {
        return lineHeight || 1.4;
    }

    private getFontFamily(fonteId: Fonte) {
        switch (fonteId) {
            case 1:
                return "Calibri";
            case 2:
                return "Space Mono";
            case 3:
                return "Courier Prime";
            case 4:
                return "Helvetica";
            case 5:
                return "Times";
            default:
                return "Arial";
        }
    }

    private flexBaseStyle(override: any = {}) {
        return [{ display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }, override];
    }

    private get isVertical() {
        const formato = Number(this.model.formatoImpressao);
        return formato === FormatoImpressaoRotulo.Vertical || formato === FormatoImpressaoRotulo.VerticalInvertido;
    }

    private get isVerticalInvertido() {
        return Number(this.model.formatoImpressao) === FormatoImpressaoRotulo.VerticalInvertido;
    }

    private get frameExternoStyle() {
        if (this.isVertical) {
            return this.flexBaseStyle({
                fontSize: `${this.scaleFactor * 1}mm`,
                width: `${this.model.largura}em`,
                height: `${this.model.altura}em`,
                alignItems: "flex-start",
                breakInside: "avoid",
                overflow: "hidden",
            });
        }

        return this.flexBaseStyle({
            // O tamanho da fonte (que é herdado pelos elementos filhos) é a unidade base para o tamanho de todos outros elementos
            fontSize: `${this.scaleFactor * 1}mm`,
            width: `${this.model.largura}em`,
            height: `${this.model.altura}em`,
            alignItems: "flex-start",
            breakInside: "avoid",
        });
    }

    private get frameInternoStyle() {
        if (this.isVertical) {
            return this.flexBaseStyle({
                flex: "1",
                width: "100%",
                height: "100%",
                margin: "0",
                flexWrap: "nowrap",
                flexDirection: "column",
                boxSizing: "border-box",
                overflow: "hidden",
                alignItems: "stretch",
                justifyContent: "flex-start",
            });
        }

        return this.flexBaseStyle({
            flex: "1",
            margin: `${this.model.paddingVertical || 0}em  ${this.model.paddingHorizontal || 0}em`,
            flexWrap: "wrap",
            flexDirection:
                this.model.posicaoQrCode == PosicaoRotulo.Cima || this.model.posicaoQrCode == PosicaoRotulo.Baixo
                    ? "column"
                    : "row",
        });
    }

    private get rowContainerStyle() {
        if (this.isVertical) {
            return {
                display: "flex",
                flexDirection: "column" as const,
                flex: "1",
                overflow: "hidden",
                boxSizing: "border-box" as const,
                alignItems: "stretch",
                writingMode: this.isVertical ? this.verticalWritingMode : ("horizontal-tb" as const),
                ...(this.isVertical && {
                    paddingTop: `${this.model.paddingVertical || 0}em`,
                    paddingBottom: `${this.model.paddingVertical || 0}em`,
                    paddingLeft: `${this.model.paddingHorizontal || 0}em`,
                    paddingRight: `${this.model.paddingHorizontal || 0}em`,
                }),
            };
        }

        return {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            flex: "1",
            // Sem minWidth o `min-width: auto` do flex item mantém o tamanho mínimo do conteúdo, que pode não caber
            // ao lado do logo/QR code. Como o frame interno tem `flex-wrap: wrap`, o bloco inteiro dos campos quebra
            // para a linha de baixo do logo e some por trás do `overflow: hidden` do frame externo.
            minWidth: 0,
        };
    }

    private get rotationStyle() {
        return {
            width: "100%",
            height: "100%",
        };
    }

    private get logoStyle() {
        return this.flexBaseStyle({
            width: `${this.model.larguraLogo}em`,
            height: `${this.model.alturaLogo}em`,
            order: this.model.posicaoLogo == PosicaoRotulo.Esquerda ? 0 : 1,
            backgroundColor: !this.CalledByShortCut ? "lightgoldenrodyellow" : "transparent",
        });
    }

    private get qrCodeStyle() {
        return this.flexBaseStyle({
            marginLeft: "auto",
            width: `${this.model.larguraCode}em`,
            height: `${this.model.alturaCode}em`,
            order:
                this.model.posicaoQrCode == PosicaoRotulo.Esquerda || this.model.posicaoQrCode == PosicaoRotulo.Cima
                    ? 0
                    : 1,
            backgroundColor: !this.CalledByShortCut ? "lightgoldenrodyellow" : "transparent",
        });
    }

    private get observacaoManipulacaoStyle() {
        if (this.isVertical) {
            return this.flexBaseStyle({
                fontFamily: this.getFontFamily(this.model.fonteRotulo),
                fontSize: `${this.adjustFontSize(this.model.observacaoFonte)}em`,
                fontWeight: this.model.observacaoNegrito ? "bold" : "normal",
                lineHeight: `${this.adjustLineHeight(this.model.paddingLine)}em`,
                overflow: "hidden",
                alignItems: "flex-start",
                justifyContent: "flex-start",
            });
        }

        return this.flexBaseStyle({
            fontFamily: this.getFontFamily(this.model.fonteRotulo),
            fontSize: `${this.adjustFontSize(this.model.observacaoFonte)}em`,
            fontWeight: this.model.observacaoNegrito ? "bold" : "normal",
        });
    }

    private get infoFranquiaStyle() {
        if (this.isVertical) {
            return this.flexBaseStyle({
                justifyContent: "flex-start",
                alignItems: "flex-start",
                overflow: "hidden",
                flex: this.isVertical ? "0 0 auto" : undefined,
            });
        }

        return this.flexBaseStyle({
            justifyContent: "flex-start",
            width: "100%",
        });
    }

    private get spanStyle() {
        if (this.isVertical) {
            return {
                fontFamily: this.getFontFamily(this.model.fonteRotulo),
                fontSize: `${this.adjustFontSize(this.model.fonteDadosFranquia)}em`,
                lineHeight: `${this.adjustLineHeight(this.model.paddingLine)}em`,
                width: "100%",
                height: "auto",
                display: "block",
                overflow: "hidden",
                textAlign:
                    this.model.posicaoDadosFranquia === PosicaoDadosFranquia.Direita
                        ? "right"
                        : this.model.posicaoDadosFranquia === PosicaoDadosFranquia.Centro
                        ? "center"
                        : "left",
            };
        }

        return {
            fontFamily: this.getFontFamily(this.model.fonteRotulo),
            fontSize: `${this.adjustFontSize(this.model.fonteDadosFranquia)}em`,
            lineHeight: `${this.adjustLineHeight(this.model.paddingLine)}em`,
            width: "100%",
            textAlign:
                this.model.posicaoDadosFranquia === PosicaoDadosFranquia.Direita
                    ? "right"
                    : this.model.posicaoDadosFranquia === PosicaoDadosFranquia.Centro
                    ? "center"
                    : "left",
        };
    }

    private getLinhaStyle() {
        if (this.isVertical) {
            return this.flexBaseStyle({
                width: "auto",
                height: this.isVertical ? "100%" : "auto",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                overflow: "hidden",
                flex: "0 0 auto",
            });
        }

        return this.flexBaseStyle();
    }

    private getCampoStyle(campo: ConfiguracaoRotuloCamposModel) {
        if (this.isVertical) {
            return this.flexBaseStyle({
                flex: this.isVertical ? "1" : campo.quantidadeColunas,
                width: "100%",
                height: "auto",
                alignItems: "flex-start",
                fontFamily: this.getFontFamily(this.model.fonteRotulo),
                fontSize: `${this.adjustFontSize(campo.fonte)}em`,
                fontWeight: campo.negrito ? "bold" : "normal",
                lineHeight: `${this.adjustLineHeight(this.model.paddingLine)}em`,
                minHeight:
                    campo.campo?.toLowerCase() == "texto livre"
                        ? `${this.adjustLineHeight(this.model.paddingLine)}em`
                        : "auto",
                display: "block",
                overflow: "hidden",
                whiteSpace: "normal",
                overflowWrap: "break-word",
                wordBreak: "normal",
                textAlign:
                    campo.alinhamento == Alinhamento.Esquerda
                        ? "left"
                        : campo.alinhamento == Alinhamento.Direita
                        ? "right"
                        : "center",
            });
        }

        return this.flexBaseStyle({
            flex: campo.quantidadeColunas, // quantas colunas o campo ocupa
            justifyContent: "flex-start",
            fontFamily: this.getFontFamily(this.model.fonteRotulo),
            fontSize: `${this.adjustFontSize(campo.fonte)}em`,
            fontWeight: campo.negrito ? "bold" : "normal",
            lineHeight: `${this.adjustLineHeight(this.model.paddingLine)}em`,
            minHeight:
                campo.campo?.toLowerCase() == "texto livre"
                    ? `${this.adjustLineHeight(this.model.paddingLine)}em`
                    : "auto",
            display: "block",
            // overflowWrap: "normal",
            // overflowX: "hidden",
            // overflowY: "hidden",
            // textWrap: "wrap",
            // wordBreak: "normal",
            textAlign:
                campo.alinhamento == Alinhamento.Esquerda
                    ? "left"
                    : campo.alinhamento == Alinhamento.Direita
                    ? "right"
                    : "center",
        });
    }

    @Watch("model.dadosId", { immediate: true })
    @Watch("model.posicaoQrCode", { immediate: true })
    @Watch("model.conteudoQrCode", { immediate: true })
    private async onChangeRotuloId() {
        if (this.model.dadosId && this.model.posicaoQrCode != PosicaoRotulo.Nenhuma) {
            if (this.model.posicaoQrCode == PosicaoRotulo.Baixo || this.model.posicaoQrCode == PosicaoRotulo.Cima) {
                this.barCodeBase64 = await this.codigosIdentificacaoService
                    .getBarCode(this.model.dadosId.toString())
                    .resolveWithText();
            } else {
                let dadoEncode: string;
                if (this.model.conteudoQrCode === ConteudoQrCode.Codigo) {
                    dadoEncode = `${this.codigoQrValue}`;
                } else {
                    dadoEncode = `${process.env.VUE_APP_ROOT_API}/InformacaoProduto/Manipulacao?key=${this.manipulacaoKey}`;
                }
                this.qrCodeBase64 = await this.codigosIdentificacaoService.getQrCode(dadoEncode).resolveWithText();
            }
        }
    }

    private get verticalWritingMode() {
        return this.isVerticalInvertido ? "vertical-lr" : "vertical-rl";
    }
}

